import * as React from "react";
import {FontAwesomeIcon}        from "@fortawesome/react-fontawesome";
import {faChevronLeft} from "@fortawesome/free-solid-svg-icons";

import "./property-wizard.scss";
import "./results-pg.scss";

import {cs}               from "@Common/react/chain-services";
import {UseState}         from "@Common/react/use-state";
import {consumeContext}   from "@Common/react/context";
import {isDevXd}          from "@Common/ui-components/debug/is-dev";
import {ButtonSpinner}    from "@Common/ui-components/loading-spinners/button-spinner";
import {ScreenLoader}     from "@Common/ui-components/loading-spinners/screen-loader";
import {AiSparklesSvg}    from "ai-banner/ai-sparkles-svg";
import {FieldResultsCard} from "./field-results-card";
import { Load2 } from "@Common/react/load2";

export const ResultsPage = ({
    idOrder,
    item,
    propertyData,
    propertyWizardData,
    aggregateTextualSummary,
    eligableContentFields,
    eligableSpecFields,
    propertyWizardInsertValues
}) => cs(
    consumeContext("apis"),
    ["loading", ({}, next) => UseState({
        initValue: false,
        next
    })],
    ["handleBackToEdit", ({apis, loading}, next) => next(async () => {
        // TODO: replace with a custom alert modal
        let text = "This will clear the results on this page. Are you sure you want to go back to the edit page?"
        if ( confirm(text) ) {
            loading.onChange(true);
            await apis.next.gpt.savePropertyWizardData({
                idOrder: parseInt(idOrder),
                data: {...propertyData.value, aggregateSummary: null},
                generatedResults: null,
            });
            await propertyWizardData.reload();
            loading.onChange(false);
        }
    })],
    ["fieldSuggestions", ({}, next) => UseState({
        initValue: (() => {
            try {
                return typeof propertyData?.value?.generatedResults === "string"
                    ? JSON.parse(propertyData?.value?.generatedResults)
                    : propertyData?.value?.generatedResults;
            } catch (error) {
                return null;
            }
        })(),
        next
    })],
    (({fieldSuggestions, apis}, next) => Load2({
        _key: propertyWizardData.value,
        fetch: async () => {
            if (propertyWizardData.loading || fieldSuggestions?.value?.length) {
                return;
            }
            const suggestions = [];
            let numOfFieldsRun = 0;
            // Using while loop to allow fields to be processed sequentially & allow for asyncronous calls
            // Not all loops are created equal. There may be some performance improvements to be made still.
            while (numOfFieldsRun < [...eligableContentFields, ...eligableSpecFields].length) {
                const field = [...eligableContentFields, ...eligableSpecFields][numOfFieldsRun];

                try {
                    let timeout, failureValue="ERROR: timeout", timeLimit=26000;
                    const timeoutPromise = new Promise((resolve, reject) => {
                        timeout = setTimeout(() => {
                            resolve(failureValue);
                        }, timeLimit);
                    });
                    let task = apis.next.gpt.generateFieldSuggestions({
                        idOrder: parseInt(idOrder),
                        numOfSuggestions: 3,
                        idProductType: parseInt(item.idProductType),
                        field: {
                            idPrimary: field.idPrimary,
                            fieldKey: field.fieldKey,
                            displayLabel: field.displayLabel,
                            maxLength: field.maxLength,
                            enableLimitOverride: field.enableLimitOverride,
                        },
                    });
                    let results = await Promise.race([task, timeoutPromise]);
                    if(results.error || typeof results == "string") {
                        isDevXd() && console.error(`Error processing field ${field.displayLabel}:`);
                        suggestions.push({
                            content : "",
                            displayLabel : field.displayLabel,
                            finishReason : "error",
                            idPrimary : field.idPrimary,
                            error: "Failed to generate suggestions",
                        })
                    } else {
                        suggestions.push(...results);
                        fieldSuggestions.onChange(results);
                    }
                } catch (error) {
                    console.error(`Error processing field ${field.fieldKey}:`, error);
                    suggestions.push({
                        content : "",
                        displayLabel : field.displayLabel,
                        finishReason : "error",
                        idPrimary : field.idPrimary,
                        error: "Failed to generate suggestions",
                    })
                }
                numOfFieldsRun++;
            }
            await apis.next.gpt.savePropertyWizardData({
                idOrder: parseInt(idOrder),
                data: {...propertyData.value},
                aggregateSummary: aggregateTextualSummary.value,
                generatedResults: suggestions.flat(),
            });
            fieldSuggestions.onChange(suggestions.flat());
        },
        next
    })),

    ({loading, handleBackToEdit, fieldSuggestions}) => (
        <div className="results-page-t87">
            {loading.value ?
            // TODO: Replace loader with AI twinkle
                <div className="page-loader"><ScreenLoader loaderColor="#6937B7"/></div>
            : <>
                <div className="back-to-edit"
                    onClick={() => handleBackToEdit()}
                >
                    <span>
                        <FontAwesomeIcon icon={faChevronLeft} />
                    </span>
                    <span>
                        Edit Property Context
                    </span>
                </div>
                {[...eligableContentFields, ...eligableSpecFields].map( field =>
                    <FieldResultsCard
                        idOrder={idOrder}
                        item={item}
                        field={field}
                        initFieldSuggestions={fieldSuggestions?.value?.filter(suggestion =>
                            suggestion.idPrimary === field.idPrimary
                        )}
                        propertyWizardInsertValues={propertyWizardInsertValues}
                    />
                )}
            </>}
        </div>
    )
)